import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import { Checkbox } from "../ui/checkbox";
import { FieldError } from "../common/States";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { VisibilityPicker, RatingInput, CategoryPicker, IdentityBlock } from "./FormParts";
import AttachmentPicker from "./AttachmentPicker";
import SubmissionSuccess from "./SubmissionSuccess";
import { CriteriaBlock, QuestionsBlock } from "./SectorFields";

const today = () => new Date().toISOString().slice(0, 10);

function validate(f, user, sector) {
  const e = {};
  (sector?.questions || []).forEach((q) => { if (q.required && !f.answers[q.id]) e[q.id] = "Réponse requise"; });
  if (!f.rating) e.rating = "Choisissez une note de 1 à 5 étoiles";
  if (!f.category) e.category = "Choisissez une catégorie";
  if (f.message.trim().length < 5) e.message = "Votre commentaire doit contenir au moins 5 caractères";
  if (f.experience_date && f.experience_date > today()) e.experience_date = "La date ne peut pas être dans le futur";
  if (!user && !f.anonymous) {
    if (!f.contact_name.trim()) e.contact_name = "Votre nom est requis";
    if (!f.contact_email.trim() && !f.contact_phone.trim()) e.contact = "Indiquez un email ou un téléphone";
    else if (f.contact_email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.contact_email)) e.contact = "Email invalide";
  }
  return e;
}

const Select = ({ label, value, onChange, options, empty, testId }) => (
  <div>
    <Label className="mb-1.5 block">{label}</Label>
    <select className="field-select" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}>
      <option value="">{empty}</option>
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  </div>
);

export default function FeedbackForm({ company, agencies = [], services = [], preset = {}, qrCode, source = "web", sectorForm }) {
  const { user } = useAuth();
  const [token, setToken] = useState(null);
  const [honeypot, setHoneypot] = useState("");
  const [f, setF] = useState({ visibility: "public", rating: 0, category: "", title: "", message: "", experience_date: "", criteria: {}, answers: {},
    anonymous: false, urgent: false, contact_name: "", contact_email: "", contact_phone: "", agency_id: preset.agency_id || "", service_id: preset.service_id || "" });
  const [files, setFiles] = useState([]);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const privateCats = sectorForm?.private_categories || [];
  const setCategory = (c) => setF((s) => ({ ...s, category: c, visibility: c === "reclamation" || privateCats.includes(c) ? "private" : s.visibility }));
  const visibleServices = services.filter((s) => !f.agency_id || !s.agency_id || s.agency_id === f.agency_id);
  const loadToken = () => api.get("/public/form-token").then(({ data }) => setToken(data.token)).catch(() => setToken(null));
  useEffect(() => { loadToken(); }, []);

  const submit = async (ev) => {
    ev.preventDefault();
    const e = validate(f, user, sectorForm);
    setErrors(e);
    if (Object.keys(e).length) { toast.error("Vérifiez les champs signalés"); return; }
    setSending(true);
    try {
      const payload = { ...f, criteria: Object.fromEntries(Object.entries(f.criteria).filter(([, v]) => v)), answers: Object.fromEntries(Object.entries(f.answers).filter(([, v]) => v !== "" && v !== 0)),
        company_id: company.id, qr_code: qrCode || null, agency_id: f.agency_id || null, service_id: f.service_id || null,
        title: f.title || null, experience_date: f.experience_date || null, source, form_token: token, website: honeypot || null, marketing_consent: !!f.marketing_consent };
      const fd = new FormData();
      fd.append("payload", JSON.stringify(payload));
      files.forEach((file) => fd.append("files", file));
      const { data } = await api.post("/public/feedbacks", fd);
      setResult(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiError(err));
      if ([409, 422].includes(err.response?.status) && /formulaire/i.test(formatApiError(err))) loadToken();
    } finally { setSending(false); }
  };

  if (result) return <SubmissionSuccess result={result} loggedIn={!!user} />;

  return (
    <form onSubmit={submit} className="touch-form card-surface space-y-6 p-5 sm:p-8" data-testid="feedback-form" noValidate>
      <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" value={honeypot} onChange={(e) => setHoneypot(e.target.value)}
        className="absolute -left-[9999px] h-0 w-0 opacity-0" data-testid="feedback-honeypot" />
      <div><Label className="mb-2 block">Type de message</Label><VisibilityPicker value={f.visibility} onChange={set("visibility")} forcedPrivate={f.category === "reclamation" || f.urgent || privateCats.includes(f.category)} /></div>
      {sectorForm?.sensitive && <p className="rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="sector-privacy-note">Secteur à confidentialité renforcée : n'indiquez ni données médicales, ni numéros de dossier, ni identités. Seule votre note est publiée, jamais votre texte ni votre nom.</p>}
      <div><Label className="mb-1 block">Votre note</Label><RatingInput value={f.rating} onChange={set("rating")} /><FieldError>{errors.rating}</FieldError></div>
      <div><Label className="mb-2 block">Catégorie</Label><CategoryPicker value={f.category} onChange={setCategory} extra={sectorForm?.categories || []} /><FieldError>{errors.category}</FieldError></div>
      {sectorForm?.criteria?.length > 0 && <CriteriaBlock criteria={sectorForm.criteria} value={f.criteria} onChange={set("criteria")} />}
      {sectorForm?.questions?.length > 0 && <QuestionsBlock questions={sectorForm.questions} value={f.answers} onChange={set("answers")} errors={errors} />}
      {(agencies.length > 0 || visibleServices.length > 0) && (
        <div className="grid gap-4 sm:grid-cols-2">
          {agencies.length > 0 && <Select label="Agence" testId="feedback-agency-select" value={f.agency_id} empty="Non précisée"
            onChange={(v) => setF((s) => ({ ...s, agency_id: v, service_id: "" }))} options={agencies.map((a) => [a.id, `${a.name}${a.city ? ` — ${a.city}` : ""}`])} />}
          {visibleServices.length > 0 && <Select label="Service concerné" testId="feedback-service-select" value={f.service_id} empty="Non précisé"
            onChange={set("service_id")} options={visibleServices.map((s) => [s.id, s.name])} />}
        </div>
      )}
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="sm:col-span-2"><Label className="mb-1.5 block">Titre (facultatif)</Label><Input maxLength={120} value={f.title} onChange={(e) => set("title")(e.target.value)} placeholder="Résumez en quelques mots" data-testid="feedback-title-input" /></div>
        <div><Label className="mb-1.5 block">Date de l'expérience</Label><Input type="date" max={today()} value={f.experience_date} onChange={(e) => set("experience_date")(e.target.value)} data-testid="feedback-date-input" /><FieldError>{errors.experience_date}</FieldError></div>
      </div>
      <div>
        <Label htmlFor="fb-message" className="mb-1.5 block">Votre commentaire</Label>
        <Textarea id="fb-message" rows={5} maxLength={3000} value={f.message} onChange={(e) => set("message")(e.target.value)}
          placeholder="Décrivez votre expérience…" data-testid="feedback-message-input" />
        <div className="flex justify-between"><FieldError>{errors.message}</FieldError><span className="ml-auto text-xs text-slate-400">{f.message.length}/3000</span></div>
      </div>
      <label className="flex items-start justify-between gap-4 rounded-xl border border-rose-200 bg-rose-50/50 p-4">
        <span className="text-sm"><b>Problème nécessitant une prise en charge prioritaire</b><br /><span className="text-slate-500">Situation grave ou confidentielle : votre message reste privé et n'est lu que par les responsables habilités.</span></span>
        <Switch checked={f.urgent} onCheckedChange={(v) => setF((s) => ({ ...s, urgent: v, visibility: v ? "private" : s.visibility }))} data-testid="feedback-urgent-switch" />
      </label>
      <div><Label className="mb-1.5 block">Pièces jointes</Label><AttachmentPicker files={files} onChange={setFiles} /></div>
      {user ? (
        <p className="rounded-lg bg-slate-50 px-4 py-3 text-sm text-slate-600" data-testid="feedback-logged-as">Envoyé en tant que <b>{user.name}</b>. Vos coordonnées ne sont jamais publiées.</p>
      ) : <IdentityBlock f={f} set={set} errors={errors} allowAnonymous={company.allow_anonymous !== false} />}
      {(user || (!f.anonymous && f.contact_email)) && (
        <label className="flex items-start gap-2 text-sm text-slate-600" data-testid="feedback-marketing-consent">
          <Checkbox checked={!!f.marketing_consent} onCheckedChange={(v) => setF((s) => ({ ...s, marketing_consent: !!v }))} data-testid="feedback-marketing-checkbox" />
          J'accepte de recevoir les communications de {company.name} par email (facultatif, désinscription possible à tout moment).
        </label>
      )}
      <Button type="submit" disabled={sending} className="h-12 w-full rounded-full text-base" data-testid="feedback-submit-button">
        {sending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{f.visibility === "public" ? "Envoyer mon avis" : "Envoyer mon message privé"}
      </Button>
    </form>
  );
}
